import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, inject, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { RecordService } from '../../record/record.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ILocalZipCodes } from '../interfaces/localZipCodes';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { NgxMaskDirective } from 'ngx-mask';
import { NgxPopperjsModule } from 'ngx-popperjs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { Json2Pipe } from '../json2.pipe';
import { HelperRtnsComponent } from '../helper-rtns.component';
import { WebsocketService } from '../websocket.service';
import moment from 'moment';
import { UppercaseDirective } from '../directives/uppercase.directive';
import { ILocalProviders } from '../interfaces/localProviders';
import { ILocalInsurances } from '../interfaces/localInsurances';
import { ILocalFacilities } from '../interfaces/localFacilities';
import { ILocalProductors } from '../interfaces/localProductors';
import { ILocalReferring } from '../interfaces/localReferring';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-prov-plan-fac-ref-prod',
  imports: [
    MatDialogModule
  ]
})
export class ProvPlanFacRefProdComponent {
  @Input() lstTyp!: string;
  @Output() lstDlgClose = new EventEmitter<string>();

  readonly _dialog = inject(MatDialog);

  constructor() { }

  ngOnInit() {
    const dialogRef = this._dialog.open(ProvPlanFacRefProdDialog, {
      data: {
        lstTyp: this.lstTyp
      },
      disableClose: false,  // Allow Esc to close dialog
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log(`ProvPlanFacRefProd Dialog result: ${result}`);
      this.lstDlgClose.emit(result);
    });
  }
}

// Implements Luhn's formula to calculate the check digit of a 10 digit NPI numeric string. Use 1234567000 as a valid test NPI.
export function npiCkDigitValidator(control: AbstractControl): ValidationErrors | null {
  const npi: string = control.value;

  if (!npi) {
    return {
      npiCkDigit: {
        valid: false,
        messageEn: '10 digits required',
        messageEs: '10 dígitos requisito'
      }
    };
  }

  const digits = npi.split('').map(Number);
  let sum = 0;

  // Double every other digit from the right (positions 1–8), then sum all digits
  for (let i = 0; i < 9; i++) {
    let val = digits[i];

    if ((8 - i) % 2 === 0) {
      val *= 2;
    }

    sum += Math.floor(val / 10) + (val % 10);
  }

  sum += 24; // Add 80840 prefix constant

  const expectedCheckDigit = (10 - (sum % 10)) % 10;
  const actualCheckDigit = digits[9];

  if (expectedCheckDigit !== actualCheckDigit) {
    return {
      npiCkDigit: {
        valid: false,
        messageEn: 'Invalid NPI',
        messageEs: 'NPI inválido'
      }
    };
  }

  return null;  // Valid NPI
}

export function personVsGroupValidator(lstTyp: string, engLang: boolean): ValidatorFn {
  return (form: FormGroup): Validators | null => {
    const last = form.get('last')?.value;
    const first = form.get('first')?.value;
    const corp = form.get('corp')?.value;

    if (lstTyp === 'plan' && !corp) {
      return {
        personVsGroup: {
          corpReq: true,
          message: engLang ? 'Required' : 'Requisito'
        }
      };
    }

    if ((lstTyp === 'prov' && (corp && (last || first)) || (!corp && (!last || !first)))) {
      return {
        personVsGroup: {
          corpReq: false,
          message: engLang ? 'Last/First names incompatible with Group/Corp' : 'Apellidos/Nombre incompatibles con Grupo/Corp'
        }
      };
    }

    return null;
  }
}

export function ckStateValidatorFactory(_hlp: HelperRtnsComponent, allowBlnk: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (allowBlnk && !control.value) {
      return null;
    }

    const state: string = control.value;
    const validStates = _hlp.usStates;
    // console.log('validStates: ', validStates);

    if (!validStates.includes(state)) {
      return {
        ckState: {
          valid: false,
          messageEn: `Invalid state code: ${state}`,
          messageEs: `Código de estado inválido: ${state}`
        }
      };
    }

    return null;
  };
}

@Component({
  selector: 'prov-plan-fac-ref-prod-dialog',
  templateUrl: './prov-plan-fac-ref-prod.dialog.html',
  styleUrls: ['./prov-plan-fac-ref-prod.dialog.css'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatCheckboxModule,
    MatToolbarModule,
    MatIconModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxPopperjsModule,
    Json2Pipe,
    UppercaseDirective
  ],
  // providers: [X12UtilsService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProvPlanFacRefProdDialog implements OnInit, OnDestroy {
  dialogTitle;
  sn: string;
  engLang: boolean;
  userID: string;
  spinner1: boolean = false;
  spinner2: boolean = false;
  items: any[] = [];

  refSrch: boolean = false;

  zips: ILocalZipCodes[] = [];
  cfis: {
    code: string,
    descr: string
  }[] = [];

  zipControlPhy: FormControl = new FormControl('',
    {
      validators: [
        Validators.required,
        Validators.pattern(/^[0-9]{5}(-)?[0-9]{4}$/)
      ], updateOn: 'change'
    }
  );
  zipControlPos: FormControl = new FormControl('',
    {
      validators: [
        Validators.pattern(/^[0-9]{5}(-)?[0-9]{4}$/)
      ], updateOn: 'change'
    }
  );
  searchRefCtrl: FormControl = new FormControl('',
    {
      validators: [
        Validators.pattern(/(^\d{0,10}$)|(^[A-Za-z\s,\-]{0,43}$)/i)
      ],
      updateOn: 'change'
    }
  );
  filteredZipsPhy!: Observable<any[]>;
  filteredZipsPos!: Observable<any[]>;
  filteredRefs: any[] = [];

  _dialog = inject(MatDialog);

  private _recordService = inject(RecordService);
  private _hlp = inject(HelperRtnsComponent);
  private _websocketService = inject(WebsocketService);

  sioSubscrpt: Subscription;

  @ViewChild(MatAutocompleteTrigger) trigger!: MatAutocompleteTrigger;
  @ViewChild('aliasHint') aliasFocus!: ElementRef<HTMLInputElement>;
  @ViewChild('npiHint') npiFocus!: ElementRef<HTMLInputElement>;

  constructor(
    private _dialogRef: MatDialogRef<ProvPlanFacRefProdDialog>,
    @Inject(MAT_DIALOG_DATA) public data:
      {
        lstTyp: string
      },
    private _fb: FormBuilder,
    private _cdRef: ChangeDetectorRef) {

    this._recordService.getClaimFilingIndicators().subscribe((cfis) => {
      this.cfis = cfis;
    });
    this.sn = this._recordService.sn;
    this.engLang = this._recordService.engLang;
    this.userID = this._recordService.userID;
    this.zips = [...this._recordService.localZipCodes];

    this.loadLstItems(); // Load items based on the list type
  }

  ngOnInit(): void {
    this.sioSubscrpt = this._websocketService.getMessages().subscribe((dataSet) => { // Sets listenning events

      console.log('%c' + 'dataSet RecordComponent', 'color: green; background: yellow; font-size: 14px');
      console.log(dataSet[0]?.sqlProcNm, dataSet);

      if (dataSet[0]?.sqlProcNm === 'spMB_Web_Get_ReferringList-search') {
        this.spinner2 = false;
        this.filteredRefs = dataSet.map((e: any) => {

          return {
            pKey: e.pKey,
            alias: (e.name.includes(',') ? e.name.match(/^[ \w]+,/g)?.join('').replace(',', '') : '') + ', ' + (e.name.includes(',') ? e.name.match(/,[ \w]+$/g)?.join('').replace(',', '').trim() : ''),
            npi: e.npi,
            lastReq: e.name.includes(',') ? e.name.match(/^[ \w]+,/g)?.join('').replace(',', '') : '',
            firstReq: e.name.includes(',') ? e.name.match(/,[ \w]+$/g)?.join('').replace(',', '').trim() : ''
          };
        }).filter((e: any) => +e.pKey > 0);
        this.trigger?.openPanel();
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_InsUpdProvider') {
        let pid: string;
        if (+dataSet[0]?.insertedProvID) {
          pid = dataSet[0].insertedProvID;
          this.items.push({
            pKey: pid,
            alias: this.alias.value,
            npi: this.npi.value,
            last: this.last.value,
            first: this.first.value,
            corp: this.corp.value,
            phyAd1: this.phyAd1.value,
            phyAd2: this.phyAd2.value,
            phyZip: this.phyZip.value,
            phyCity: this.phyCity.value,
            phySt: this.phySt.value,
            taxo: this.taxo.value,
            poAd1: this.poAd1.value,
            poAd2: this.poAd2.value,
            poZip: this.poZip.value,
            poCity: this.poCity.value,
            poSt: this.poSt.value,
            taxId: this.taxId.value,
            taxIdType: this.taxIdType.value,
            inactive: String(+this.inactive.value ? true : false),
            email: this.email.value,
            phone: this.phone.value
          });
          this.pKey.setValue(pid);  // Set the form pKey to the new provider ID so it shows as selected on screen
        }
        if (+dataSet[0]?.updatedProvID) {
          pid = dataSet[0]?.updatedProvID;
          const p = this.items.find(e => e.pKey === dataSet[0].updatedProvID);
          if (p) {
            p.alias = this.alias.value;
            p.npi = this.npi.value;
            p.last = this.last.value;
            p.first = this.first.value;
            p.corp = this.corp.value;
            p.phyAd1 = this.phyAd1.value;
            p.phyAd2 = this.phyAd2.value;
            p.phyZip = this.phyZip.value;
            p.phyCity = this.phyCity.value;
            p.phySt = this.phySt.value;
            p.taxo = this.taxo.value;
            p.poAd1 = this.poAd1.value ? this.poAd1.value : '';
            p.poAd2 = this.poAd2.value ? this.poAd2.value : '';
            p.poZip = this.poZip.value ? this.poZip.value : '';
            p.poCity = this.poCity.value ? this.poCity.value : '';
            p.poSt = this.poSt.value ? this.poSt.value : '';
            p.taxId = this.taxId.value;
            p.taxIdType = this.taxIdType.value;
            p.inactive = this.inactive.value;
            p.email = this.email.value ? this.email.value : '';
            p.phone = this.phone.value ? this.phone.value : '';
          }
        }

        const pLocal: ILocalProviders = this._recordService.localProviders.find(e => e.pKey === pid);
        if (!pLocal) { // Doesn't exists in ...localProviders[] so add it
          this._recordService.localProviders.push({
            pKey: pid,
            alias: this.alias.value.toString(),
            npi: this.npi.value.toString(),
            provName: this.corp.value?.toString() ? this.corp.value.toString() : this.last.value.toString() + ', ' + this.first.value.toString(),
            provAd1: this.phyAd1.value.toString(),
            provAd2: this.phyAd2.value.toString(),
            provZip: this.phyZip.value.toString(),
            provCity: this.phyCity.value.toString(),
            provSt: this.phySt.value.toString(),
            provTaxonomy: this.taxo.value.toString(),
            poAd1: this.poAd1.value.toString(),
            poAd2: this.poAd2.value.toString(),
            poZip: this.poZip.value.toString(),
            poCity: this.poCity.value.toString(),
            poSt: this.poSt.value.toString(),
            taxId: this.taxId.value.toString(),
            ssn: (this.taxIdType.value.toString() === 'ssn' ? '1' : '0'),
            ein: (this.taxIdType.value.toString() === 'ein' ? '1' : '0'),
            inactive: this.inactive.value ? '1' : '0',
            email: this.email.value.toString(),
            provCel: this.phone.value.toString(),
            provIsCo: this.corp.value.toString().length > 0 ? '1' : '0',
            provAcode: '',
            provTel: '',
            provFtpHost: '',
            provFtpUser: '',
            provFtpPw: '',
            lab: '',
            sqlProcNm: ''
          });
        } else if (pLocal) { // Exists in ...localProviders[] so update it
          pLocal.pKey = pid;
          pLocal.alias = this.alias.value.toString();
          pLocal.npi = this.npi.value.toString();
          pLocal.provName = this.corp.value.toString() ? this.corp.value.toString() : this.last.value.toString() + ', ' + this.first.value.toString();
          pLocal.provAd1 = this.phyAd1.value.toString();
          pLocal.provAd2 = this.phyAd2.value.toString();
          pLocal.provZip = this.phyZip.value.toString();
          pLocal.provCity = this.phyCity.value.toString();
          pLocal.provSt = this.phySt.value.toString();
          pLocal.provTaxonomy = this.taxo.value.toString();
          pLocal.poAd1 = this.poAd1.value ? this.poAd1.value.toString() : '';
          pLocal.poAd2 = this.poAd2.value ? this.poAd2.value.toString() : '';
          pLocal.poZip = this.poZip.value ? this.poZip.value.toString() : '';
          pLocal.poCity = this.poCity.value ? this.poCity.value.toString() : '';
          pLocal.poSt = this.poSt.value ? this.poSt.value.toString() : '';
          pLocal.taxId = this.taxId.value.toString();
          pLocal.ssn = this.taxIdType.value.toString() === 'ssn' ? '1' : '0';
          pLocal.ein = this.taxIdType.value.toString() === 'ein' ? '1' : '0';
          pLocal.inactive = this.inactive.value ? '1' : '0';
          pLocal.email = this.email.value ? this.email.value.toString() : '';
          pLocal.provCel = this.phone.value ? this.phone.value.toString() : '';
        }

        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_DeleteProvider') {
        if (+dataSet[0].deletedRows === 1) {
          const itmIndx = this.items.findIndex(e => e.pKey === dataSet[0].deletedProvID);
          if (itmIndx > -1) {
            this.items.splice(itmIndx, 1);
          }
          const pLocalIndx = this._recordService.localProviders.findIndex(e => e.pKey === dataSet[0].deletedProvID);
          if (pLocalIndx > -1) {
            this._recordService.localProviders.splice(pLocalIndx, 1);
          }
          this.pKey.setValue('');  // Clear the form pKey so it shows as unselected on screen; leave the form values as is in case user wants to edit/save it again

          this._recordService.showToast(
            'Ok',
            (this.engLang ? 'Provider eliminated!' : '¡Proveedor eliminado!'),
            true, false, false, true, true);
        } else {
          this._recordService.showToast(
            (this.engLang ? 'Provider not eliminated!' : '¡Proveedor no eliminado!'),
            (this.engLang ? 'Provider has active claims in system and cannot be deleted. Optionally mark it inactive.'
              : 'El proveedor tiene casos activos en el sistema y no puede ser eliminado. Opcionalmente márquelo inactivo.'),
            true, false, true, false, false);
        }
        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_InsUpdPlan') {
        let pid: string;
        if (+dataSet[0]?.insertedInID) {
          pid = dataSet[0].insertedInID;
          this.items.push({
            pKey: pid,
            alias: this.alias.value,
            corp: this.corp.value,
            payerID: this.payerID.value,
            payerID_835: this.payerID_835.value,
            cfi: this.cfi.value,
            inactive: +this.inactive.value ? true : false
          });
          this.pKey.setValue(pid);  // Set the form pKey to the new insurance ID so it shows as selected on screen
        }
        if (+dataSet[0]?.updatedInID) {
          pid = dataSet[0].updatedInID;
          const i = this.items.find(e => e.pKey === dataSet[0].updatedInID);
          if (i) {
            i.alias = this.alias.value;
            i.corp = this.corp.value;
            i.payerID = this.payerID.value;
            i.payerID_835 = this.payerID_835.value;
            i.cfi = this.cfi.value;
            i.inactive = this.inactive.value;
          }
        }

        const iLocal: ILocalInsurances = this._recordService.localInsurances.find(e => e.pKey === pid);
        if (!iLocal) { // Doesn't exists in ...localInsurances[] so add it
          this._recordService.localInsurances.push({
            pKey: pid,
            alias: this.alias.value.toString(),
            name: this.corp.value.toString(),
            payerId: this.payerID.value.toString(),
            payerId835: (this.payerID_835.value ? this.payerID_835.value.toString() : ''),
            x837clmFilInd: this.cfi.value.toString(),
            inactive: (+this.inactive.value ? '1' : '0'),
            add1: '',
            add2: '',
            city: '',
            st: '',
            zip: '',
            s1type: '',
            s33bTaxonomy: '',
            s30BalDue: '',
            x837fmtNo: '',
            x837usePrvAsRenderAt2310B: '',
            sqlProcNm: ''
          });
        } else if (iLocal) { // Exists in ...localInsurances[] so update it
          iLocal.pKey = pid;
          iLocal.alias = this.alias.value.toString();
          iLocal.name = this.corp.value.toString();
          iLocal.payerId = this.payerID.value.toString();
          iLocal.payerId835 = this.payerID_835.value.toString();
          iLocal.x837clmFilInd = this.cfi.value.toString();
          iLocal.inactive = this.inactive.value ? '1' : '0';
        }

        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_DeletePlan') {
        if (+dataSet[0].deletedRows === 1) {
          const itmIndx = this.items.findIndex(e => e.pKey === dataSet[0].deletedInID);
          if (itmIndx > -1) {
            this.items.splice(itmIndx, 1);
          }
          const iLocalIndx = this._recordService.localInsurances.findIndex(e => e.pKey === dataSet[0].deletedInID);
          if (iLocalIndx > -1) {
            this._recordService.localInsurances.splice(iLocalIndx, 1);
          }
          this.pKey.setValue('');  // Clear the form pKey so it shows as unselected on screen
        } else {
          this._recordService.showToast(
            (this.engLang ? 'Plan not eliminated!' : '¡Plan no eliminado!'),
            (this.engLang ? 'Plan has active claims in system and cannot be deleted. Optionally mark it inactive.'
              : 'El plan tiene casos activos en el sistema y no puede ser eliminado. Opcionalmente márquelo inactivo.'),
            true, false, true, false, false);
        }
        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_InsUpdFac') {
        let pid: string;
        if (+dataSet[0]?.insertedFacID) {
          pid = dataSet[0].insertedFacID;
          this.items.push({
            pKey: pid,
            alias: this.alias.value,
            corp: this.corp.value,
            npi: this.npi.value,
            inactive: this.inactive.value,
            adr1: this.phyAd1.value,
            adr2: this.phyAd2.value,
            city: this.phyCity.value,
            st: this.phySt.value,
            zip: this.phyZip.value
          });
          this.pKey.setValue(pid);  // Set the form pKey to the new insurance ID so it shows as selected on screen
        }
        if (+dataSet[0]?.updatedFacID) {
          pid = dataSet[0]?.updatedFacID;
          const f = this.items.find(e => e.pKey === dataSet[0].updatedFacID);
          if (f) {
            f.alias = this.alias.value;
            f.corp = this.corp.value;
            f.npi = this.npi.value;
            f.inactive = this.inactive.value;
            f.adr1 = this.phyAd1.value;
            f.adr2 = this.phyAd2.value;
            f.city = this.phyCity.value;
            f.st = this.phySt.value;
            f.zip = this.phyZip.value;
          }
        }

        const fLocal: ILocalFacilities = this._recordService.localFacilities.find(e => e.pKey === pid);
        if (!fLocal) { // Doesn't exists in ...localFacilities[] so add it
          this._recordService.localFacilities.push({
            pKey: pid,
            alias: this.alias.value.toString(),
            facName: this.corp.value.toString(),
            facNpi: this.npi.value.toString(),
            inactive: (this.inactive.value ? '1' : '0'),
            facAd1: this.phyAd1.value.toString(),
            facAd2: this.phyAd2.value.toString(),
            facCity: this.phyCity.value.toString(),
            facSt: this.phySt.value.toString(),
            facZip: this.phyZip.value.toString(),
            sqlProcNm: ''
          });
        } else if (fLocal) { // Exists in ...localFaciities[] so update it
          fLocal.pKey = pid;
          fLocal.alias = this.alias.value.toString();
          fLocal.facName = this.corp.value.toString();
          fLocal.facNpi = this.npi.value.toString();
          fLocal.inactive = (this.inactive.value ? '1' : '0');
          fLocal.facAd1 = this.phyAd1.value.toString();
          fLocal.facAd2 = this.phyAd2.value.toString();
          fLocal.facCity = this.phyCity.value.toString();
          fLocal.facSt = this.phySt.value.toString();
          fLocal.facZip = this.phyZip.value.toString();
        }

        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_DeleteFac') {
        if (+dataSet[0].deletedRows === 1) {
          const itmIndx = this.items.findIndex(e => e.pKey === dataSet[0].deletedFacID);
          if (itmIndx > -1) {
            this.items.splice(itmIndx, 1);
          }
          const fLocalIndx = this._recordService.localFacilities.findIndex(e => e.pKey === dataSet[0].deletedFacID);
          if (fLocalIndx > -1) {
            this._recordService.localFacilities.splice(fLocalIndx, 1);
          }
          this.pKey.setValue('');  // Clear the form pKey so it shows as unselected on screen
        } else {
          this._recordService.showToast(
            (this.engLang ? 'Facility not eliminated!' : '¡Facilidad no eliminada!'),
            (this.engLang ? 'Facility has active claims in system and cannot be deleted. Optionally mark it inactive.'
              : 'La facilidad tiene casos activos en el sistema y no puede ser eliminada. Opcionalmente márquela inactiva.'),
            true, false, true, false, false);
        }
        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_InsUpdProductor') {
        let pid: string;
        if (+dataSet[0]?.insertedProdID) {
          pid = dataSet[0].insertedProdID;
          this.items.push({
            pKey: pid,
            alias: this.alias.value,
            npi: this.npi.value,
            last: this.lastReq.value,
            first: this.firstReq.value,
            taxId: this.taxId.value,
            taxo: this.taxo.value,
            inactive: String(+this.inactive.value ? true : false)
          });
          this.pKey.setValue(pid);  // Set the form pKey to the new provider ID so it shows as selected on screen
        }
        if (+dataSet[0]?.updatedProdID) {
          pid = dataSet[0].updatedProdID;
          const p = this.items.find(e => e.pKey === dataSet[0].updatedProdID);
          if (p) {
            p.alias = this.alias.value;
            p.npi = this.npi.value;
            p.lastReq = this.lastReq.value;
            p.firstReq = this.firstReq.value;
            p.taxo = this.taxo.value;
            p.taxId = this.taxId.value;
            p.inactive = this.inactive.value;
          }
        }

        const pLocal: ILocalProductors = this._recordService.localProductors.find(e => e.pKey === pid);
        if (!pLocal) { // Doesn't exists in ...localProductors[] so add it
          this._recordService.localProductors.push({
            pKey: pid,
            alias: this.alias.value.toString(),
            npi: this.npi.value.toString(),
            prodName: this.lastReq.value.toString() + ', ' + this.firstReq.value.toString(),
            taxo: this.taxo.value.toString(),
            ss: this.taxId.value.toString(),
            phone: this.phone.value.toString(),
            email: this.email.value.toString(),
            inactive: this.inactive.value ? '1' : '0',
            sqlProcNm: ''
          });
        } else if (pLocal) { // Exists in ...localProviders[] so update it
          pLocal.pKey = pid;
          pLocal.alias = this.alias.value.toString();
          pLocal.npi = this.npi.value.toString();
          pLocal.prodName = this.lastReq.value.toString() + ', ' + this.firstReq.value.toString();
          pLocal.taxo = this.taxo.value.toString();
          pLocal.ss = this.taxId.value.toString();
          pLocal.inactive = this.inactive.value ? '1' : '0';
        }

        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_DeleteProductor') {
        if (+dataSet[0].deletedRows === 1) {
          const itmIndx = this.items.findIndex(e => e.pKey === dataSet[0].deletedProdID);
          if (itmIndx > -1) {
            this.items.splice(itmIndx, 1);
          }
          const dLocalIndx = this._recordService.localProductors.findIndex(e => +e.pKey === +dataSet[0].deletedProdID);
          if (dLocalIndx > -1) {
            this._recordService.localProductors.splice(dLocalIndx, 1);
          }
          this.pKey.setValue('');  // Clear the form pKey so it shows as unselected on screen
        } else {
          this._recordService.showToast(
            (this.engLang ? 'Rendering not eliminated!' : '¡Productor no eliminado!'),
            (this.engLang ? 'Rendering has active claims in system and cannot be deleted. Optionally mark it inactive.'
              : 'El productor tiene casos activos en el sistema y no puede ser eliminado. Opcionalmente márquelo inactivo.'),
            true, false, true, false, false);
        }
        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_InsUpdReferring') {
        let pid: string;
        if (+dataSet[0]?.insertedRefID) {
          pid = dataSet[0].insertedRefID;
          this.items.push({
            pKey: pid,
            npi: this.npi.value,
            lastReq: this.lastReq.value,
            firstReq: this.firstReq.value,
            inactive: +this.inactive.value ? true : false
          });
          this.pKey.setValue(pid);  // Set the form pKey to the new referring ID so it shows as selected on screen
        }
        if (+dataSet[0]?.updatedRefID) {
          pid = dataSet[0].updatedRefID;
          const r = this.items.find(e => e.pKey === dataSet[0].updatedRefID);
          if (r) {
            r.npi = this.npi.value;
            r.lastReq = this.lastReq.value;
            r.firstReq = this.firstReq.value;
            r.inactive = this.inactive.value;
          }
        }

        const rLocal: ILocalReferring = this._recordService.localReferring.find(e => e.pKey === pid);
        if (!rLocal) { // Doesn't exists in ...localReferring[] so add it
          this._recordService.localReferring.push({
            pKey: pid,
            alias: this.lastReq.value.toString() + ', ' + this.firstReq.value.toString(),
            npi: this.npi.value.toString(),
            name: this.lastReq.value.toString() + ', ' + this.firstReq.value.toString(),
            inactive: this.inactive.value ? '1' : '0',
            ad1: '',
            ad2: '',
            city: '',
            st: '',
            zip: '',
            sqlProcNm: ''
          });
        } else if (rLocal) { // Exists in ...localReferring[] so update it
          rLocal.pKey = pid;
          rLocal.alias = this.lastReq.value.toString().trim() + ', ' + this.firstReq.value.toString().trim();
          rLocal.npi = this.npi.value.toString();
          rLocal.name = this.lastReq.value.toString().trim() + ', ' + this.firstReq.value.toString().trim();
          rLocal.inactive = this.inactive.value ? '1' : '0';
        }

        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

      if (dataSet[0]?.sqlProcNm === 'spMB_Sio_DeleteReferring') {
        if (+dataSet[0].deletedRows === 1) {
          const itmIndx = this.items.findIndex(e => e.pKey === dataSet[0].deletedRefID);
          if (itmIndx > -1) {
            this.items.splice(itmIndx, 1);
          }
          const rLocalIndx = this._recordService.localReferring.findIndex(e => e.pKey === dataSet[0].deletedRefID);
          if (rLocalIndx > -1) {
            this._recordService.localReferring.splice(rLocalIndx, 1);
          }
          this.pKey.setValue('');  // Clear the form pKey so it shows as unselected on screen
        } else {
          this._recordService.showToast(
            (this.engLang ? 'Referring not eliminated!' : '¡Referente no eliminado!'),
            (this.engLang ? 'Referring has active claims in system and cannot be deleted. Optionally mark it inactive.'
              : 'El referente tiene casos activos en el sistema y no puede ser eliminado. Opcionalmente márquelo inactivo.'),
            true, false, true, false, false);
        }
        this.spinner1 = false;
        this._cdRef.detectChanges();  // Otherwise, spinner1 won't dissapear
      }

    });

    this.filteredZipsPhy = this.zipControlPhy.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );

    this.filteredZipsPos = this.zipControlPos.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );

    this.form.markAllAsTouched(); // Mark all controls as touched to trigger & show validation messages
  }

  ngOnDestroy(): void {
    this.sioSubscrpt.unsubscribe();
  }

  onClick_close(event) {
    console.log('onClick_close: ', event);
    this._dialog.closeAll(); // Close all dialogs
  }

  onClick_select(event) {
    // console.dir(event);
    const ref: any = this.filteredRefs.find(e => e.pKey === event);
    if (ref) {
      if (!this._recordService.localReferring.find(e => e.pKey === event)) { // Add to localReferring so referring alias is shown in record component

        const newRef = {
          name: ref.alias,
          npi: ref.npi,
          pKey: ref.pKey,
          alias: ref.alias,
          ad1: null,
          ad2: null,
          city: null,
          st: null,
          zip: null,
          inactive: null,
          sqlProcNm: ''
        };

        this._recordService.localReferring = [  // Update the localReferring list with the new Ref this way so the data changes everywhere
          ...this._recordService.localReferring,
          newRef
        ];
      }

      const selectedRef = {
        npi: ref.npi,
        pKey: ref.pKey,
        alias: ref.alias,
      };
      this._dialogRef.close(selectedRef);
    }
  }

  onClick_add() {
    switch (this.data.lstTyp) {
      case 'prov':
        this.form.setValue(this.emptyFormProv);
        break;
      case 'plan':
        this.form.setValue(this.emptyFormPlan);
        break;
      case 'fac':
        this.form.setValue(this.emptyFormFac);
        break;
      case 'ref':
        this.form.setValue(this.emptyFormRef);
        this.npiFocus.nativeElement.focus(); // Focus on the 1st form input, only for referring
        return;
      case 'prod':
        this.form.setValue(this.emptyFormProd);
        break;
      default:
        console.error('onClick_add: Unknown list type', this.data.lstTyp);
    }
    this.aliasFocus.nativeElement.focus(); // Focus on the 1st form input all but referring
  }

  onClick_delete(event) {
    // console.log('onClick_delete: pKey', this.pKey.value);
    let q: string = '';
    if (this.pKey.value) {
      switch (this.data.lstTyp) {
        case 'prov':
          q += 'spMB_Sio_DeleteProvider @sn = \'' + this.sn + '\', @provID = \'' + this.pKey.value + '\';'
          console.log('q:', q);
          break;
        case 'plan':
          q += 'spMB_Sio_DeletePlan @sn = \'' + this.sn + '\', @inID = \'' + this.pKey.value + '\';'
          console.log('q:', q);
          break;
        case 'prod':
          q += 'spMB_Sio_DeleteProductor @sn = \'' + this.sn + '\', @prodID = \'' + this.pKey.value + '\';'
          console.log('q:', q);
          break;
        case 'fac':
          q += 'spMB_Sio_DeleteFac @sn = \'' + this.sn + '\', @facID = \'' + this.pKey.value + '\';'
          console.log('q:', q);
          break;
        case 'ref':
          q += 'spMB_Sio_DeleteReferring @sn = \'' + this.sn + '\', @refID = \'' + this.pKey.value + '\';'
          console.log('q:', q);
          break;
        default:
          console.error('onClick_delete()-ERROR, data.lstTyp =', this.data.lstTyp);
      }

      this.spinner1 = true;
      this._websocketService.sendChat('query', this._recordService.sn, q);
    } else {
      this.onClick_add();
    }
  }

  onClick_save(event) {
    console.log('onClick_save: ', event);
    let q: string = '';
    switch (this.data.lstTyp) {
      case 'prov':
        const ini = this.first.value.toString().match(/ [A-Z]$/g) ? this.first.value.toString().match(/ [A-Za-z]$/g)[0].replace(' ', '') : '';
        q += 'spMB_Sio_InsUpdProvider @sn = \'' + this.sn + '\', '
          + '@provID = \'' + (this.pKey.value ? + this.pKey.value : '0')
          + '\', @provLNm = \'' + this.alias.value
          + '\', @provCoNm = \'' + (this.corp.value ? this._hlp.escApos(this.corp.value.toString()) : '')
          + '\', @provLastNm = \'' + (this.last.value ? this._hlp.escApos(this.last.value.toString()) : '')
          + '\', @provFstNm = \'' + (this.first.value ? this._hlp.escApos(this.first.value.toString().replace(/ \w$/, '')) : '')
          + '\', @provMidInit = \'' + ini
          + '\', @provAd1 = \'' + this._hlp.escApos(this.phyAd1.value.toString())
          + '\', @provAd2 = \'' + (this.phyAd2.value ? this._hlp.escApos(this.phyAd2.value.toString()) : '')
          + '\', @provCity = \'' + this.phyCity.value
          + '\', @provSt = \'' + this.phySt.value
          + '\', @provZip = \'' + this.phyZip.value
          + '\', @provPOAd1 = \'' + (this.poAd1.value ? this._hlp.escApos(this.poAd1.value.toString()) : '')
          + '\', @provPOAd2 = \'' + (this.poAd2.value ? this._hlp.escApos(this.poAd2.value.toString()) : '')
          + '\', @provPOCity = \'' + (this.poCity.value ? this.poCity.value : '')
          + '\', @provPOSt = \'' + (this.poSt.value ? this.poSt.value : '')
          + '\', @provPOZip = \'' + (this.poZip.value ? this.poZip.value : '')
          + '\', @provCell = \'' + (this.phone.value ? this.fmtRawPhoneNumber(this.phone.value.toString()) : '')
          + '\', @provNPI = \'' + this.npi.value
          + '\', @provSSN = \'' + (this.taxIdType.value === 'ssn' ? '1' : '0')
          + '\', @provEIN = \'' + (this.taxIdType.value === 'ein' ? '1' : '0')
          + '\', @provSS = \'' + this.fmtRawTaxId(this.taxId.value.toString(), this.taxIdType.value.toString())
          + '\', @provSpec = \'' + this._hlp.escApos(this.taxo.value.toString())
          + '\', @provEmail = \'' + (this.email.value ? this._hlp.escApos(this.email.value.toString()) : '')
          + '\', @provUserId = \'' + this.userID
          + '\', @provInactive = \'' + (this.inactive.value ? '1' : '0')
          + '\', @provDtTmEdit = \'' + moment(new Date()).format('YYYY-MM-DD HH:mm:ss')
          + '\';'
        console.log('q:', q);
        break;

      case 'plan':
        q += 'spMB_Sio_InsUpdPlan @sn = \'' + this.sn + '\', '
          + '@inID = \'' + (this.pKey.value ? + this.pKey.value : '0')
          + '\', @inLNm = \'' + this.alias.value
          + '\', @inNm = \'' + (this.corp.value ? this._hlp.escApos(this.corp.value.toString()) : '')
          + '\', @inInactive = \'' + (this.inactive.value ? '1' : '0')
          + '\', @inPayID = \'' + (this.payerID.value ? this._hlp.escApos(this.payerID.value.toString()) : '')
          + '\', @inPayID835 = \'' + (this.payerID_835.value ? this._hlp.escApos(this.payerID_835.value.toString()) : '')
          + '\', @inClmFilInd = \'' + this.cfi.value
          + '\', @inUserId = \'' + this.userID
          + '\', @updDtTmUtc = \'' + moment(new Date()).format('YYYY-MM-DD HH:mm:ss')
          + '\';'
        console.log('q:', q);
        break;

      case 'fac':
        q += 'spMB_Sio_InsUpdFac @sn = \'' + this.sn + '\', '
          + '@facID = \'' + (this.pKey.value ? + this.pKey.value : '0')
          + '\', @facLNm = \'' + this.alias.value
          + '\', @facNm = \'' + (this.corp.value ? this._hlp.escApos(this.corp.value.toString()) : '')
          + '\', @facInactive = \'' + (this.inactive.value ? '1' : '0')
          + '\', @facNPI = \'' + this.npi.value
          + '\', @facAd1 = \'' + this._hlp.escApos(this.phyAd1.value.toString())
          + '\', @facAd2 = \'' + (this.phyAd2.value ? this._hlp.escApos(this.phyAd2.value.toString()) : '')
          + '\', @facCity = \'' + this.phyCity.value
          + '\', @facSt = \'' + this.phySt.value
          + '\', @facZip = \'' + this.phyZip.value
          + '\', @facUserId = \'' + this.userID
          + '\', @facDtTmEdit = \'' + moment(new Date()).format('YYYY-MM-DD HH:mm:ss')
          + '\';'
        console.log('q:', q);
        break;

      case 'prod':
        const init = this.firstReq.value.toString().match(/ [A-Z]$/g) ? this.firstReq.value.toString().match(/ [A-Za-z]$/g)[0].replace(' ', '') : '';
        q += 'spMB_Sio_InsUpdProductor @sn = \'' + this.sn + '\', '
          + '@prodID = \'' + (this.pKey.value ? + this.pKey.value : '0')
          + '\', @prodLNm = \'' + this.alias.value
          + '\', @prodLastNm = \'' + (this.lastReq.value ? this._hlp.escApos(this.lastReq.value.toString()) : '')
          + '\', @prodFstNm = \'' + (this.firstReq.value ? this._hlp.escApos(this.firstReq.value.toString().replace(/ \w$/, '')) : '')
          + '\', @prodMidInit = \'' + init
          + '\', @prodCell = \'' + (this.phone.value ? this.fmtRawPhoneNumber(this.phone.value.toString()) : '')
          + '\', @prodNPI = \'' + this.npi.value
          + '\', @prodSS = \'' + (this.taxId.value ? this.taxId.value.toString() : '')
          + '\', @prodSpec = \'' + this._hlp.escApos(this.taxo.value.toString())
          + '\', @prodEmail = \'' + (this.email.value ? this._hlp.escApos(this.email.value.toString()) : '')
          + '\', @prodUserId = \'' + this.userID
          + '\', @prodInactive = \'' + (this.inactive.value ? '1' : '0')
          + '\', @prodDtTmEdit = \'' + moment(new Date()).format('YYYY-MM-DD HH:mm:ss')
          + '\';'
        console.log('q:', q);
        break;

      case 'ref':
        const inir = this.firstReq.value.toString().match(/ [A-Z]$/g) ? this.firstReq.value.toString().match(/ [A-Za-z]$/g)[0].replace(' ', '') : '';
        q += 'spMB_Sio_InsUpdReferring @sn = \'' + this.sn + '\', '
          + '@refID = \'' + (this.pKey.value ? + this.pKey.value : '0')
          + '\', @refLastNm = \'' + this._hlp.escApos(this.lastReq.value.toString())
          + '\', @refFstNm = \'' + this._hlp.escApos(this.firstReq.value.toString().replace(/ \w$/, ''))
          + '\', @refMidInit = \'' + inir
          + '\', @refNPI = \'' + this.npi.value
          + '\', @refUserId = \'' + this.userID
          + '\', @refInactive = \'' + (this.inactive.value ? '1' : '0')
          + '\', @refDtTmEdit = \'' + moment(new Date()).format('YYYY-MM-DD HH:mm:ss')
          + '\';'
        console.log('q:', q);
        break;

      default:
        console.error('onClick_save: Unknown list type:', this.data.lstTyp);
    }

    this.spinner1 = true;
    this._websocketService.sendChat('query', this._recordService.sn, q);
  }

  formProv = { // 1
    pKey: [''],
    alias: ['', [
      Validators.required,
      Validators.pattern(/^[ \w]{1,35}$/)
    ]],
    npi: ['', [
      (control: AbstractControl) => npiCkDigitValidator(control)]
    ],
    last: ['', [
      Validators.pattern(/^['a-zA-Z\s]*\-?$/)
    ]],
    first: ['', [
      Validators.pattern(/^[a-zA-Z\s]*$/)
    ]],
    corp: ['', [
      Validators.pattern(/^[-.&,a-zA-Z0-9\s]*$/)
    ]],
    phyAd1: ['', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z0-9\s.,'#\-]{3,30}$/i)
    ]],
    phyAd2: ['', [
      Validators.pattern(/^[a-zA-Z0-9\s#.,\-]{0,30}$/i)
    ]],
    phyZip: this.zipControlPhy,
    phyCity: ['', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z\s]{3,20}$/i)
    ]],
    phySt: ['', [
      (control: AbstractControl) => ckStateValidatorFactory(this._hlp, false)(control),
    ]],
    taxo: ['', [
      Validators.required
    ]],
    poAd1: ['', [
      Validators.pattern(/^[a-zA-Z0-9\s.,'#\-]{3,30}$/i)
    ]],
    poAd2: ['', [
      Validators.pattern(/^[a-zA-Z0-9\s#.,\-]{0,30}$/i)
    ]],
    poZip: this.zipControlPos,
    poCity: ['', [
      Validators.pattern(/^[a-zA-Z\s]{3,20}$/i)
    ]],
    poSt: ['', [
      (control: AbstractControl) => ckStateValidatorFactory(this._hlp, true)(control),
    ]],
    taxId: ['', [
      Validators.required,
      Validators.pattern(/(^\d{9}$)|(^\d{3}\-\d{2}\-\d{4}$)|(^\d{2}\-\d{7}$)/)
    ]],
    taxIdType: ['ssn'],
    email: ['', [
      Validators.email
    ]],
    phone: ['', [
      Validators.pattern(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/)
    ]],
    inactive: [false]
  }

  loadLstItems() {
    switch (this.data.lstTyp) {
      case 'prov':
        this.dialogTitle = 'Billing Providers';
        this.items = [...this._recordService.localProviders].filter(e => +e.pKey > 0).map(e => {
          return {
            pKey: e.pKey,
            alias: e.alias,
            npi: e.npi,
            last: !+e.provIsCo ? e.provName.match(/^[ \w]+,/g)?.join('').replace(',', '').trim() : '',
            first: !+e.provIsCo ? e.provName.match(/,[ \w]+$/g)?.join('').replace(',', '').trim() : '',
            corp: +e.provIsCo ? e.provName : '',
            phyAd1: e.provAd1,
            phyAd2: e.provAd2,
            phyZip: e.provZip,
            phyCity: e.provCity,
            phySt: e.provSt,
            taxo: e.provTaxonomy,
            poAd1: e.poAd1,
            poAd2: e.poAd2,
            poZip: e.poZip,
            poCity: e.poCity,
            poSt: e.poSt,
            taxId: e.taxId,
            taxIdType: +e.ssn === 1 ? 'ssn' : 'ein',
            inactive: +e.inactive === 1,
            email: e.email,
            phone: (e.provCel.trim() === '(787)   -' ? '' : e.provCel) // To avoid showing 787 alone as phone number & thus avoid the error message
          };
        });
        break;

      case 'plan':
        this.dialogTitle = this.engLang ? 'Plans' : 'Planes';
        this.items = [...this._recordService.localInsurances].filter(e => +e.pKey > 0).map(e => {
          return {
            pKey: e.pKey,
            alias: e.alias,
            corp: e.name,
            payerID: e.payerId,
            payerID_835: e.payerId835,
            cfi: e.x837clmFilInd,
            inactive: +e.inactive === 1 ? true : false,
          };
        }).filter(e => +e.pKey > 0);;
        break;

      case 'fac':
        this.dialogTitle = this.engLang ? 'Facilities' : 'Facilidades';
        this.items = [...this._recordService.localFacilities].filter(e => +e.pKey > 0).map(e => {
          return {
            pKey: e.pKey,
            alias: e.alias,
            npi: e.facNpi,
            corp: e.facName,
            phyAd1: e.facAd1,
            phyAd2: e.facAd2,
            phyZip: e.facZip,
            phyCity: e.facCity,
            phySt: e.facSt,
            inactive: +e.inactive === 1
          };
        });
        break;

      case 'ref':
        this.dialogTitle = this.engLang ? 'Referring MDs' : 'Médicos Referentes';
        this.items = [...this._recordService.localReferring].filter(e => +e.pKey > 0).map(e => {
          return {
            pKey: e.pKey,
            alias: (e.name.includes(',') ? e.name.match(/^[ \w]+,/g)?.join('').replace(',', '') : '') + ', ' + (e.name.includes(',') ? e.name.match(/,[ \w]+$/g)?.join('').replace(',', '') : ''),
            npi: e.npi,
            lastReq: e.name.includes(',') ? e.name.match(/^[ \w]+,/g)?.join('').replace(',', '') : '',
            firstReq: e.name.includes(',') ? e.name.match(/,[ \w]+$/g)?.join('').replace(',', '') : '',
            inactive: +e.inactive === 1
          };
        }).filter(e => +e.pKey > 0);
        break;

      case 'prod':
        this.dialogTitle = this.engLang ? 'Rendering' : 'Productores';
        this.items = [...this._recordService.localProductors].filter(e => +e.pKey > 0).map(e => {
          return {
            pKey: e.pKey,
            alias: e.alias,
            npi: e.npi,
            taxId: e.ss,
            taxo: e.taxo,
            lastReq: e.prodName.includes(',') ? e.prodName.match(/^[ \w]+,/g)?.join('').replace(',', '') : '',
            firstReq: e.prodName.includes(',') ? e.prodName.match(/,[ \w]+$/g)?.join('').replace(',', '') : '',
            phone: e.phone,
            email: e.email,
            inactive: +e.inactive === 1
          };
        });
        break;
      default:
        console.error('Error: loadLstItems() - Unknown list type: ', this.data.lstTyp);
    }
  }

  formPlan = { // 1
    pKey: [''],
    alias: ['', [
      Validators.required,
      Validators.pattern(/^[ \w]{1,35}$/)
    ]],
    corp: ['', [
      Validators.required,
      Validators.pattern(/^[-.&,a-zA-Z0-9\s]*$/)
    ]],
    inactive: [false],
    payerID: ['', [
      Validators.required
    ]],
    payerID_835: [''],
    cfi: ['', [
      Validators.required,
    ]]
  }

  formProd = { // 1
    pKey: [''],
    alias: ['', [
      Validators.required,
      Validators.pattern(/^[ \w]{1,35}$/)
    ]],
    npi: ['', [
      (control: AbstractControl) => npiCkDigitValidator(control)]
    ],
    lastReq: ['', [
      Validators.required,
      Validators.pattern(/^['a-zA-Z\s]*\-?$/)
    ]],
    firstReq: ['', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z\s]*$/)
    ]],
    taxo: ['', [
      Validators.required
    ]],
    taxId: ['', [
      Validators.required,
      Validators.pattern(/(^\d{9}$)|(^\d{3}\-\d{2}\-\d{4}$)|(^\d{2}\-\d{7}$)/)
    ]],
    email: ['', [
      Validators.email
    ]],
    phone: ['', [
      Validators.pattern(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/)
    ]],
    inactive: [false]
  }

  formRef = { // 1
    pKey: [''],
    // alias: ['', [
    //   Validators.required,
    //   Validators.pattern(/^[, \w]{1,35}$/)
    // ]],
    npi: ['', [
      (control: AbstractControl) => npiCkDigitValidator(control)]
    ],
    lastReq: ['', [
      Validators.required,
      Validators.pattern(/^['a-zA-Z\s]*\-?$/)
    ]],
    firstReq: ['', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z\s]*$/)
    ]],
    inactive: [false]
  }

  formFac = { // 1
    pKey: [''],
    alias: ['', [
      Validators.required,
      Validators.pattern(/^[ \w]{1,35}$/)
    ]],
    npi: ['', [
      (control: AbstractControl) => npiCkDigitValidator(control)]
    ],
    corp: ['', [
      Validators.required,
      Validators.pattern(/^[-.&,a-zA-Z0-9\s]*$/)
    ]],
    phyAd1: ['', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z0-9\s.,'#\-]{3,30}$/i)
    ]],
    phyAd2: ['', [
      Validators.pattern(/^[a-zA-Z0-9\s#.,\-]{0,30}$/i)
    ]],
    phyZip: this.zipControlPhy,
    phyCity: ['', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z\s]{3,20}$/i)
    ]],
    phySt: ['', [
      (control: AbstractControl) => ckStateValidatorFactory(this._hlp, false)(control),
    ]],
    inactive: [false]
  }

  formObj(): any {
    switch (this.data.lstTyp) {
      case 'prov':  // Providers
        return this.formProv;
      case 'plan':  // Plans
        return this.formPlan;
      case 'fac':  // Facilities
        return this.formFac;
      case 'ref':  // Referring MDs
        return this.formRef;
      case 'prod':  // Productors
        return this.formProd;
    }
  }

  formValidatorObj(): any {
    switch (this.data.lstTyp) {
      case 'prov':  // Providers
        return { validators: [personVsGroupValidator(this.data.lstTyp, this._recordService.engLang)], updateOn: 'blur' };
      default:
        return {};
    }
  }

  form = this._fb.group(this.formObj(),
    this.formValidatorObj());

  get pKey() {  // 2
    return this.form.get('pKey');
  }

  get alias() { // 2
    return this.form.get('alias');
  }

  get npi() { // 2
    return this.form.get('npi');
  }

  get last() {  // 2
    return this.form.get('last');
  }

  get lastReq() {  // 2
    return this.form.get('lastReq');
  }

  get first() { // 2
    return this.form.get('first');
  }

  get firstReq() { // 2
    return this.form.get('firstReq');
  }

  get corp() { // 2
    return this.form.get('corp');
  }

  get phyAd1() { // 2
    return this.form.get('phyAd1');
  }

  get phyAd2() { // 2
    return this.form.get('phyAd2');
  }

  get phyCity() { // 2
    return this.form.get('phyCity');
  }

  get phySt() { // 2
    return this.form.get('phySt');
  }

  get phyZip() { // 2
    return this.form.get('phyZip');
  }

  get taxo() { // 2
    return this.form.get('taxo');
  }

  get poAd1() { // 2
    return this.form.get('poAd1');
  }

  get poAd2() { // 2
    return this.form.get('poAd2');
  }

  get poCity() { // 2
    return this.form.get('poCity');
  }

  get poSt() { // 2
    return this.form.get('poSt');
  }

  get poZip() { // 2
    return this.form.get('poZip');
  }

  get taxId() { // 2
    return this.form.get('taxId');
  }

  get taxIdType() { // 2
    return this.form.get('taxIdType');
  }

  get inactive() { // 2
    return this.form.get('inactive');
  }

  get email() { // 2
    return this.form.get('email');
  }

  get phone() { // 2
    return this.form.get('phone');
  }

  get payerID() { // 2
    return this.form.get('payerID');
  }

  get payerID_835() { // 2
    return this.form.get('payerID_835');
  }

  get cfi() { // 2
    return this.form.get('cfi');
  }

  emptyFormProv: any = {  // 4
    "pKey": null,
    "alias": null,
    "npi": null,
    "last": null,
    "first": null,
    "corp": null,
    "phyAd1": null,
    "phyAd2": null,
    "phyZip": null,
    "phyCity": null,
    "phySt": null,
    "taxo": null,
    "poAd1": null,
    "poAd2": null,
    "poZip": null,
    "poCity": null,
    "poSt": null,
    "taxId": null,
    "taxIdType": 'ssn',
    "inactive": false,
    "email": null,
    "phone": null
  }

  emptyFormPlan: any = {  // 4
    "pKey": null,
    "alias": null,
    "corp": null,
    "inactive": false,
    "payerID": null,
    "payerID_835": null,
    "cfi": null
  }

  emptyFormRef: any = {  // 4
    "pKey": null,
    "lastReq": null,
    "firstReq": null,
    "npi": null,
    "inactive": false
  }

  emptyFormFac: any = {  // 4
    "pKey": null,
    "alias": null,
    "npi": null,
    "corp": null,
    "phyAd1": null,
    "phyAd2": null,
    "phyZip": null,
    "phyCity": null,
    "phySt": null,
    "inactive": false
  }

  emptyFormProd: any = {  // 4
    "pKey": null,
    "alias": null,
    "npi": null,
    "lastReq": null,
    "firstReq": null,
    "taxo": null,
    "taxId": null,
    "inactive": false,
    "email": null,
    "phone": null
  }

  private _filter(value: string): any[] { // Filter function for autocomplete
    const filterValue = value.toLowerCase();
    console.log('filterValue: ', filterValue);
    console.log('filteredZipsPhy', this.filteredZipsPhy);
    return this.zips.filter(z =>
      z.zipCode.includes(filterValue) || z.zipCity.toLowerCase().includes(filterValue)
    );
  }

  onChange_item(event: any) { // 3
    this.form.markAsPristine();
    const i = this.items.findIndex(e => e.pKey === event);
    if (Number(i) > -1) {
      switch (this.data.lstTyp) {
        case 'prov':
          this.form.setValue(this.emptyFormProv);
          this.pKey.setValue(this.items[i].pKey);
          this.alias.setValue(this.items[i].alias);
          this.npi.setValue(this.items[i].npi);
          this.last.setValue(this.items[i].last);
          this.first.setValue(this.items[i].first);
          this.corp.setValue(this.items[i].corp);
          this.phyAd1.setValue(this.items[i].phyAd1);
          this.phyAd2.setValue(this.items[i].phyAd2);
          this.phyZip.setValue(this.items[i].phyZip);
          this.phyCity.setValue(this.items[i].phyCity);
          this.phySt.setValue(this.items[i].phySt);
          this.taxo.setValue(this.items[i].taxo);
          this.poAd1.setValue(this.items[i].poAd1);
          this.poAd2.setValue(this.items[i].poAd2);
          this.poZip.setValue(this.items[i].poZip);
          this.poCity.setValue(this.items[i].poCity);
          this.poSt.setValue(this.items[i].poSt);
          this.taxId.setValue(this.items[i].taxId);
          this.taxIdType.setValue(this.items[i].taxIdType);
          this.inactive.setValue(this.items[i].inactive);
          this.email.setValue(this.items[i].email);
          this.phone.setValue(this.items[i].phone);
          break;

        case 'plan':
          this.form.setValue(this.emptyFormPlan);
          this.pKey.setValue(this.items[i].pKey);
          this.alias.setValue(this.items[i].alias);
          this.corp.setValue(this.items[i].corp);
          this.payerID.setValue(this.items[i].payerID);
          this.payerID_835.setValue(this.items[i].payerID_835);
          this.cfi.setValue(this.items[i].cfi);
          this.inactive.setValue(this.items[i].inactive);
          break;

        case 'ref':
          this.form.setValue(this.emptyFormRef);
          this.pKey.setValue(this.items[i].pKey);
          this.lastReq.setValue(this.items[i].lastReq);
          this.firstReq.setValue(this.items[i].firstReq);
          this.npi.setValue(this.items[i].npi);
          this.inactive.setValue(this.items[i].inactive);
          break;

        case 'fac':
          this.form.setValue(this.emptyFormFac);
          this.pKey.setValue(this.items[i].pKey);
          this.alias.setValue(this.items[i].alias);
          this.npi.setValue(this.items[i].npi);
          this.corp.setValue(this.items[i].corp);
          this.phyAd1.setValue(this.items[i].phyAd1);
          this.phyAd2.setValue(this.items[i].phyAd2);
          this.phyZip.setValue(this.items[i].phyZip);
          this.phyCity.setValue(this.items[i].phyCity);
          this.phySt.setValue(this.items[i].phySt);
          this.inactive.setValue(this.items[i].inactive);
          break;

        case 'prod':
          this.form.setValue(this.emptyFormProd);
          this.pKey.setValue(this.items[i].pKey);
          this.alias.setValue(this.items[i].alias);
          this.npi.setValue(this.items[i].npi);
          this.lastReq.setValue(this.items[i].lastReq);
          this.firstReq.setValue(this.items[i].firstReq);
          this.taxo.setValue(this.items[i].taxo);
          this.taxId.setValue(this.items[i].taxId);
          this.inactive.setValue(this.items[i].inactive);
          this.email.setValue(this.items[i].email);
          this.phone.setValue(this.items[i].phone);
      }
    }
  }

  onSelectZipPhy(selectedZip: string) {
    if (selectedZip?.length >= 5) {
      selectedZip = selectedZip.trim();
      if (this.zipControlPhy) {
        this.zipControlPhy.setValue(selectedZip);
        const i = this.zips.findIndex(e => e.zipCode.substring(0, 5).startsWith(selectedZip.substring(0, 5)));
        if (Number(i) > -1) {
          this.phyCity.setValue(this.zips[i].zipCity);
          this.phySt.setValue(this.zips[i].zipState);
        }
      }
    }
  }

  onSelectZipPos(selectedZip: string) {
    if (selectedZip?.length >= 5) {
      selectedZip = selectedZip.trim();
      if (this.zipControlPos) {
        this.zipControlPos.setValue(selectedZip);
        const i = this.zips.findIndex(e => e.zipCode.substring(0, 5).startsWith(selectedZip.substring(0, 5)));
        if (Number(i) > -1) {
          this.poCity.setValue(this.zips[i].zipCity);
          this.poSt.setValue(this.zips[i].zipState);
        }
      }
    }
  }

  onTaxIdMaskChange(event: any) {
    this.taxIdType.setValue(event.value);
    console.log('this.taxIdType.value:', this.taxIdType.value);
  }

  onClick_showPopperInactive(event: any) {
    console.log('onClick_showPopperInactive: ', event);
  }

  onChange_cfi(event: any) {
    const i = this.cfis.findIndex(e => e.code === event);
    if (Number(i) > -1) {
      this.cfi.setValue(this.cfis[i].code);
    } else {
      this.cfi.setValue('');
    }
  }

  onClick_showRefSearch(event: any) {
    this.refSrch = !this.refSrch;
    if (this.refSrch) {
      setTimeout(() => {  // Waits for the DOM to be updated before focusing in next tick
        const el = document.getElementById('searchRefInput');
        if (el) {
          el.focus();
        }
      }, 0);
    }
  }

  onChange_searchRefCtrl(pK: string) {
    console.log('onChange_searchRefCtrl: ', pK);
    const ref = this.filteredRefs.find(e => e.pKey === pK);
    console.log('Found Ref:', ref);
    if (ref) {
      if (!this._recordService.localReferring.find(e => e.pKey === pK)) {

        const newRef = {
          name: ref.alias,
          npi: ref.npi,
          pKey: ref.pKey,
          alias: ref.alias,
          ad1: null,
          ad2: null,
          city: null,
          st: null,
          zip: null,
          inactive: null,
          sqlProcNm: ''
        };

        this._recordService.localReferring = [  // Update the localReferring list with the new Ref this way so the data changes everywhere
          ...this._recordService.localReferring,
          newRef
        ];
      }
      this.searchRefCtrl.setValue(ref.alias); // Set the matInput searchRefCtrl value to the selected Ref's alias and NPI after clicking on selection opts
      this.loadLstItems(); // Reload items to include the new Ref
      this.onChange_item(pK);
    }
  }

  onEnter_searchRefCtrl(txt: string) {  // Go to the back end DB to search for the Ref
    // console.log('onEnter_searchRefCtrl: ', txt);
    if (txt.length > 3 && !this.searchRefCtrl?.errors) {
      let q: string = "Exec spMB_Web_Get_ReferringList @sn = '" + this.sn;
      const c: number = txt.indexOf(',');
      if (Number.isInteger(+txt)) { // Search by NPI
        q += "', @npi = '" + txt + "'";
      } else if (c > -1) { // Contains a comma delimiter for last & first
        q += "', @lastNm = '" + txt.substring(0, c).trim() + "'";
        q += ", @firstNm = '" + txt.substring(c + 1, txt.length).trim() + "'";
      } else {  // Search only last name
        q += "', @lastNm = '" + txt + "'";
      }
      this.spinner2 = true;
      this._websocketService.sendChat('query', this._recordService.sn, q);
    }
  }

  fmtRawPhoneNumber(rawNo: string): string {
    if (!rawNo) {
      return '';
    }
    const cleaned = ('' + rawNo).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ')' + match[2] + '-' + match[3];
    }
    return rawNo;
  }

  fmtRawTaxId(taxId: string, type: string): string {
    if (!taxId) {
      return '';
    }
    const cleaned = ('' + taxId).replace(/\D/g, '');
    let match;
    if (type === 'ssn') {
      match = cleaned.match(/^(\d{3})(\d{2})(\d{4})$/);
      if (match) {
        return match[1] + '-' + match[2] + '-' + match[3];
      }
    } else {
      match = cleaned.match(/^(\d{2})(\d{7})$/);
      if (match) {
        return match[1] + '-' + match[2];
      }
    }
    return taxId;
  }

}

